import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-border bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        /** Glass / clay dome — secondary CTAs on dashboard cards and panels. */
        clay:
          "clay-btn border-border/10 bg-transparent text-secondary-foreground shadow-none hover:text-foreground",
        /** Top bar and shell chrome — circular icon affordances. */
        shell:
          "rounded-full hover:bg-accent text-foreground aria-pressed:bg-primary/15 aria-pressed:text-primary aria-pressed:shadow-[0_0_12px_-2px_hsl(var(--neon-blue)/0.45)]",
        /** Header filter pills (e.g. include expired). */
        pill:
          "rounded-full text-xs font-medium text-muted-foreground hover:bg-muted sm:text-sm aria-pressed:bg-accent aria-pressed:text-accent-foreground",
      },
      size: {
        default: "h-9 px-3.5 py-2",
        sm: "h-8 px-2.5 text-xs",
        xs: "h-7 rounded-md px-2 text-[11px]",
        lg: "h-10 px-6",
        icon: "size-9 shrink-0",
        iconSm: "size-8 shrink-0",
        touch: "h-11 px-3.5 md:h-9",
        touchIcon: "size-11 shrink-0 md:size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

/** Icon-only control with consistent touch target and shrink behavior. */
const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "outline", size = "icon", ...props }, ref) => (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn("shrink-0", className)}
      {...props}
    />
  ),
);
IconButton.displayName = "IconButton";

export { Button, IconButton, buttonVariants };
